import React, { ComponentType, ReactNode, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { PRODUCT_IDS } from './consts';
import { useCurrentProductIdStore } from './stores/CurrentProductIdStore';

export type ProductIdsType = typeof PRODUCT_IDS;
export type ProductIdType = ProductIdsType[keyof ProductIdsType];

type CurrentProductIdContextType = {
  productId?: ProductIdType;
};

/**
 * Context for current product ID.
 */
const CurrentProductIdContext =
  getOrCreateGlobalContext<CurrentProductIdContextType>(
    CONTEXT_NAMES.CurrentProductIdContext,
  );

const useCurrentProductIdStoreWithValidation = () =>
  useStoreWithValidation<CurrentProductIdContextType>(
    CONTEXT_NAMES.CurrentProductIdContext,
    {
      hook: 'useCurrentProductIdContext',
      provider: 'CurrentProductIdProvider',
      context: CurrentProductIdContext,
    },
  );

/**
 * Returns the current product ID from context. Must be used inside `CurrentProductIdProvider`.
 */
export const useCurrentProductId = () => {
  const { productId } = useCurrentProductIdStoreWithValidation();
  return productId;
};

type CurrentProductIdProviderProps = {
  productId?: ProductIdType;
  children: ReactNode;
};

export const CurrentProductIdProvider: ComponentType<
  CurrentProductIdProviderProps
> = ({ children, productId: productIdProp }) => {
  useCurrentProductIdStore(productIdProp);

  const value = useMemo(() => ({ productId: productIdProp }), [productIdProp]);

  return (
    <CurrentProductIdContext.Provider value={value}>
      {children}
    </CurrentProductIdContext.Provider>
  );
};

CurrentProductIdProvider.displayName = 'CurrentProductIdProvider';
